import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  MapPin,
  UserRound,
  Headset,
  ChevronRight,
  TicketPercent,
  Receipt,
  KeyRound,
  Clock,
  Loader2,
  PackageCheck,
  CreditCard,
  ArrowRight,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { orderApi } from "../../../api/order";

/* ============ Theme helpers ============ */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const useSurfaces = (theme?: any) => {
  const bgHex = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bgHex)) < 0.5;
  return {
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
    surface3: isDark ? "rgba(255,255,255,.085)" : "rgba(0,0,0,.085)",
    divider: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    textPrimary: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
    gradient: theme?.gradientHero ?? "linear-gradient(90deg,#0ea5e9,#22c55e)",
    bg: bgHex,
    softShadow: isDark
      ? "0 10px 28px rgba(0,0,0,.28)"
      : "0 10px 28px rgba(0,0,0,.10)",
  };
};
const panel = (
  lvl: 1 | 2 | 3,
  S: ReturnType<typeof useSurfaces>
): React.CSSProperties => ({
  backgroundColor:
    lvl === 1 ? S.surface1 : lvl === 2 ? S.surface2 : S.surface3,
  borderColor: S.divider,
  borderWidth: 1,
  boxShadow: S.softShadow,
});

/* ============ Durum badge'leri ============ */
const statusMap: Record<number, { t: string; cls: string }> = {
  0:  { t: "Alındı",             cls: "bg-blue-100 text-blue-800" },
  6:  { t: "Kargoda",            cls: "bg-cyan-100 text-cyan-800" },
  7:  { t: "Teslim Edildi",      cls: "bg-emerald-100 text-emerald-800" },
  8:  { t: "İptal",              cls: "bg-rose-100 text-rose-800" },
  9:  { t: "İade",               cls: "bg-rose-100 text-rose-800" },
  4:  { t: "Teslim Edilemedi",   cls: "bg-amber-100 text-amber-900" },
  18: { t: "Teslim Edilemedi",   cls: "bg-amber-100 text-amber-900" },
};
const payMap: Record<number, { t: string; cls: string }> = {
  0: { t: "Ödenmedi", cls: "bg-gray-100 text-gray-700" },
  1: { t: "Ödendi",   cls: "bg-emerald-100 text-emerald-800" },
  2: { t: "Kısmi",    cls: "bg-amber-100 text-amber-900" },
  3: { t: "İade",     cls: "bg-rose-100 text-rose-800" },
};
const Badge: React.FC<{ map: typeof statusMap; code: number }> = ({ map, code }) => {
  const m = map[code] ?? { t: `#${code}`, cls: "bg-gray-100 text-gray-700" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.cls}`}>
      {m.t}
    </span>
  );
};

const fmt = (v: number, ccy = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);

/* ============ QuickTile ============ */
const QuickTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  S: ReturnType<typeof useSurfaces>;
}> = ({ icon, label, onClick, S }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center gap-2 rounded-xl py-3 transition hover:shadow-sm border"
    style={{ ...panel(1, S) }}
  >
    <span
      className="inline-flex items-center justify-center w-11 h-11 rounded-full"
      style={{ backgroundColor: S.surface2, color: S.textPrimary }}
    >
      {icon}
    </span>
    <span className="text-xs font-medium" style={{ color: S.textPrimary }}>
      {label}
    </span>
  </button>
);

/* ============ RowItem ============ */
const RowItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  desc?: string;
  onClick: () => void;
  S: ReturnType<typeof useSurfaces>;
  disabled?: boolean;
}> = ({ icon, title, desc, onClick, S, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition"
    style={{ ...panel(1, S), opacity: disabled ? 0.5 : 1 }}
  >
    <div className="flex items-center gap-3">
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-full"
        style={{ backgroundColor: S.surface2, color: S.textPrimary }}
      >
        {icon}
      </span>
      <div className="text-left">
        <div className="text-sm font-medium" style={{ color: S.textPrimary }}>
          {title}
        </div>
        {desc && (
          <div className="text-xs" style={{ color: S.textSecondary }}>
            {desc}
          </div>
        )}
      </div>
    </div>
    <ChevronRight size={18} style={{ color: S.textSecondary }} />
  </button>
);

/* ============ Page ============ */
const PanelOverview: React.FC = () => {
  const nav = useNavigate();
  const { theme } = useTheme();
  const { customer } = useCustomerAuth();
  const S = useSurfaces(theme);

  /* Son siparişi çek */
  const [lastOrder, setLastOrder] = useState<any | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    if (!customer?.id) return;
    (async () => {
      setOrdersLoading(true);
      try {
        const res = await orderApi.list({
          musteriId: customer.id,
          page: 1,
          pageSize: 1,
          sort: "yeni",
        });
        if (res.success && res.data?.items?.length) {
          setLastOrder(res.data.items[0]);
        }
      } catch {
        // sessizce geç
      } finally {
        setOrdersLoading(false);
      }
    })();
  }, [customer?.id]);

  return (
    <div style={{ backgroundColor: S.bg }}>
      <div className="space-y-4 md:space-y-6">

        {/* Hızlı İşlemler */}
        <div className="rounded-2xl p-3 md:p-4 border" style={{ ...panel(2, S) }}>
          <p className="text-xs font-semibold mb-3 px-1" style={{ color: S.textSecondary }}>
            Hızlı İşlemler
          </p>
          <div className="grid grid-cols-4 gap-2">
            <QuickTile icon={<Package size={18} />}   label="Siparişler" onClick={() => nav("/musteri/panel/siparisler")} S={S} />
            <QuickTile icon={<MapPin size={18} />}    label="Adresler"   onClick={() => nav("/musteri/panel/adresler")}   S={S} />
            <QuickTile icon={<UserRound size={18} />} label="Profil"     onClick={() => nav("/musteri/panel/profil")}     S={S} />
            <QuickTile icon={<Headset size={18} />}   label="Destek"     onClick={() => nav("/iletisim")}                 S={S} />
          </div>
        </div>

        {/* Kampanya baneri */}
        <div className="rounded-2xl p-[1px]" style={{ background: S.gradient }}>
          <div
            className="rounded-2xl p-4"
            style={{
              background: "rgba(255,255,255,.08)",
              backdropFilter: "blur(6px)",
              color: "#fff",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20">
                <TicketPercent size={18} />
              </span>
              <div>
                <div className="text-sm font-semibold">Kampanyaları kaçırmayın</div>
                <div className="text-[13px] opacity-90">
                  Bildirimleri açın, fırsatlardan anında haberdar olun.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Son Sipariş */}
        <div className="rounded-2xl p-3 md:p-4 space-y-2 border" style={{ ...panel(2, S) }}>
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-sm font-semibold" style={{ color: S.textPrimary }}>
              Son Sipariş
            </span>
            <button
              onClick={() => nav("/musteri/panel/siparisler")}
              className="inline-flex items-center gap-1 text-xs"
              style={{ color: S.accent }}
            >
              Tümünü gör <ArrowRight size={13} />
            </button>
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={20} className="animate-spin" style={{ color: S.textSecondary }} />
            </div>
          ) : lastOrder ? (
            <button
              onClick={() => nav(`/musteri/panel/siparisler/${lastOrder.siparisId}`)}
              className="w-full rounded-xl border p-3 text-left transition"
              style={{ ...panel(1, S) }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate" style={{ color: S.textPrimary }}>
                    Sipariş #{lastOrder.siparisNo}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: S.textSecondary }}>
                    <Clock size={11} className="inline mr-1" />
                    {new Date(lastOrder.siparisTarihi).toLocaleDateString("tr-TR", {
                      day: "numeric", month: "long", year: "numeric",
                    })}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: S.textSecondary }}>
                      <PackageCheck size={13} />
                      <Badge map={statusMap} code={lastOrder.durumKodu} />
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: S.textSecondary }}>
                      <CreditCard size={13} />
                      <Badge map={payMap} code={lastOrder.odemeDurumu} />
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold text-sm" style={{ color: S.textPrimary }}>
                    {fmt(lastOrder.genelToplamBrut, lastOrder.paraBirimiKodu)}
                  </div>
                  <ChevronRight size={16} style={{ color: S.textSecondary, marginLeft: "auto" }} />
                </div>
              </div>
            </button>
          ) : (
            <div className="rounded-xl border px-4 py-5 text-center" style={{ ...panel(1, S) }}>
              <Receipt size={28} className="mx-auto mb-2 opacity-40" style={{ color: S.textSecondary }} />
              <p className="text-sm" style={{ color: S.textSecondary }}>
                Henüz siparişiniz yok.
              </p>
              <button
                onClick={() => nav("/products")}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-white"
                style={{ background: S.gradient }}
              >
                Alışverişe Başla <ArrowRight size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Hesap & Ayarlar */}
        <div className="rounded-2xl p-3 md:p-4 space-y-2 border" style={{ ...panel(2, S) }}>
          <div className="px-1 pb-1 text-sm font-semibold" style={{ color: S.textPrimary }}>
            Hesap &amp; Ayarlar
          </div>
          <RowItem
            icon={<UserRound size={18} />}
            title="Profil Bilgileri"
            desc="Ad, e-posta, telefon"
            onClick={() => nav("/musteri/panel/profil")}
            S={S}
          />
          <RowItem
            icon={<KeyRound size={18} />}
            title="Şifre Değiştir"
            desc="Hesap güvenliğinizi güncelleyin"
            onClick={() => nav("/musteri/panel/profil")}
            S={S}
          />
          <RowItem
            icon={<MapPin size={18} />}
            title="Adreslerim"
            desc="Teslimat ve fatura adresleriniz"
            onClick={() => nav("/musteri/panel/adresler")}
            S={S}
          />
        </div>

        {/* Alt not */}
        <p className="text-center text-[12px]" style={{ color: S.textSecondary }}>
          Yardım mı lazım?{" "}
          <button
            onClick={() => nav("/iletisim")}
            className="underline"
            style={{ color: S.accent }}
          >
            İletişime geçin
          </button>
          .
        </p>
      </div>
    </div>
  );
};

export default PanelOverview;
